//asian-paints-frontend/src/pages/project/ProjectAdd.js
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import EngineerLayout from "../../layouts/EngineerLayout";
import {
  ClipboardList, CalendarDays, HardHat, Users,
  FolderKanban, RefreshCw, Settings, CheckCircle2, Pencil, ArrowLeft,
} from "lucide-react";

// ─── Design tokens (identical to your file) ───────────────────────────────────
const T = {
  violet: "#5C2D91",
  violetMid: "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red: "#C8102E",
  redLight: "#FCEAED",
  orange: "#F58220",
  orangeLight: "#FEF0E6",
  gold: "#C87800",
  goldLight: "#FEF5DC",
  green: "#22C55E",
  greenDark: "#16A34A",
  greenLight: "#F0FDF4",
  blue: "#2563EB",
  blueLight: "#EFF6FF",
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  dark: "#0F172A",
  muted: "#64748B",
  faint: "#94A3B8",
  border: "#E2E8F0",
  borderSolid: "#E2E8F0",
};

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  border: `1.5px solid ${T.border}`,
  borderRadius: 12,
  padding: "11px 14px",
  fontSize: 13,
  color: T.dark,
  background: T.surfaceAlt,
  outline: "none",
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 400,
  transition: "border-color 0.18s, box-shadow 0.18s",
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: T.muted,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  display: "block",
  marginBottom: 7,
  fontFamily: "'Montserrat', sans-serif",
};

// ─── Section header ─────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: T.violetLight,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, flexShrink: 0,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.dark, fontFamily: "'Montserrat',sans-serif" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

// ─── Form card with top accent strip ─────────────────────────────────────────
function FormCard({ children, accentLeft, accentRight }) {
  return (
    <div style={{
      background: T.surface,
      borderRadius: 16,
      border: `1px solid ${T.borderSolid}`,
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      marginBottom: 16,
    }}>
      <div style={{ height: 3, background: `linear-gradient(90deg,${accentLeft || T.violet},${accentRight || T.orange})` }} />
      <div style={{ padding: "22px 24px" }}>{children}</div>
    </div>
  );
}

// ─── Input with focus ring ────────────────────────────────────────────────────
function Input({ label, ...props }) {
  const [focused, setFocused] = useState(false);

  // For date fields: open the native calendar when clicking anywhere in the box,
  // not just the small calendar icon.
  const handleClick = (e) => {
    if (props.type === "date" && !props.readOnly && e.target.showPicker) {
      try { e.target.showPicker(); } catch (err) { /* ignore unsupported browsers */ }
    }
  };

  return (
    <div>
      {label && <label style={labelStyle}>{label}</label>}
      <input
        {...props}
        style={{
          ...inputStyle,
          borderColor: focused ? T.violet : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(92,45,145,0.1)` : "none",
          background: props.readOnly ? T.violetFaint : T.surfaceAlt,
          color: props.readOnly ? T.muted : T.dark,
          cursor: props.readOnly ? "not-allowed" : (props.type === "date" ? "pointer" : "text"),
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={handleClick}
      />
    </div>
  );
}

// ─── Select with focus ring ───────────────────────────────────────────────────
function Select({ label, children, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <label style={labelStyle}>{label}</label>}
      <select
        {...props}
        style={{
          ...inputStyle,
          borderColor: focused ? T.violet : T.border,
          boxShadow: focused ? `0 0 0 3px rgba(92,45,145,0.1)` : "none",
          cursor: "pointer",
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 14px center",
          paddingRight: 36,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        {children}
      </select>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// HER COMPONENT — all her state, hooks, handlers 100% unchanged
// Only the JSX return block has been restyled
// ═════════════════════════════════════════════════════════════════════════════
function ProjectAssignment({ layout }) {

  // ── ALL HER STATE — 100% UNCHANGED ───────────────────────────────────────
  const [assignments, setAssignments] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [segments, setSegments] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [editAssignmentId, setEditAssignmentId] = useState(null);
  const [userRegions, setUserRegions] = useState([]);

  // FIX (root cause of the bug): this used to read `window.location.search`
  // directly in the render body:
  //     const queryParams = new URLSearchParams(window.location.search);
  //     const editId = queryParams.get("edit_id");
  // window.location is a raw browser API — React has no way to know it changed,
  // so this component was NOT guaranteed to re-render just because the sidebar
  // navigated from "?edit_id=140" to no query string at all. That's why the
  // earlier "reset form when editId is falsy" fix sometimes never even ran.
  // useSearchParams() is the React-Router-aware equivalent: it subscribes to
  // the router's location state directly, so this component is guaranteed to
  // re-render (and editId is guaranteed to update) on every URL change,
  // including query-string-only changes on the same path.
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit_id");

const [formData, setFormData] = useState({
    project: "",
    projectId: "",
    t_id: "",
    total_area_sqmtr: "",
    region: "",
manager: localStorage.getItem("manager_id") || localStorage.getItem("id"),
    engineer: localStorage.getItem("id") || "",
    startDate: "",
    endDate: "",
    segment: "",
    product_category: "",
    full_name: "",
    designation: "",
    phone: "",
    email: "",
    supervisor_name: "", supervisor_email: "", supervisor_phone: "",
    applicator_name: "", applicator_email: "", applicator_phone: "",
    created_by: localStorage.getItem("id"),
  });

const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tidError, setTidError] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  // auto-dismiss success toast after 4 seconds — same as UserMaster
  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(""), 4000);
    return () => clearTimeout(t);
  }, [saveSuccess]);


  // ── ALL HER useEffects — 100% UNCHANGED ──────────────────────────────────
  useEffect(() => {
    if (!editId) {
      // Reset back to blank defaults whenever there's no edit_id in the URL —
      // covers both a fresh page load AND navigating client-side away from an
      // Edit Project page (e.g. clicking "Project Add" in the sidebar while
      // ?edit_id=140 was still set), so stale data from a previous edit never
      // lingers on screen. Same shape used after a successful "Save Project".
      setEditAssignmentId(null);
      setFormData({
        project: "",
        projectId: "",
        t_id: "",
        total_area_sqmtr: "",
        region: "",
        manager: localStorage.getItem("manager_id") || localStorage.getItem("id"),
        engineer: localStorage.getItem("id") || "",
        startDate: "",
        endDate: "",
        segment: "",
        product_category: "",
        full_name: "",
        designation: "",
        phone: "",
        email: "",
        supervisor_name: "", supervisor_email: "", supervisor_phone: "",
        applicator_name: "", applicator_email: "", applicator_phone: "",
        created_by: localStorage.getItem("id"),
      });
      setTidError(false);
      setSaveError("");
      setLoading(false);
      return;
    }
    const loadEditData = async () => {
      try {
        setLoading(true);
        const projRes = await API.get(`/projects/${editId}`);
        
        if (projRes.data?.success) {
          const proj = projRes.data.project;
          console.log("[ProjectEdit] Loaded project details:", proj);
          setEditAssignmentId(proj.id || null);
setFormData({
  project: proj.title || "",
  projectId: proj.project_uid || "",
  region: proj.region || "",
  manager: proj.manager_id || "",
  engineer: proj.engineer_id || "",
  startDate: proj.start_date ? proj.start_date.split("T")[0] : "",
  endDate: proj.end_date ? proj.end_date.split("T")[0] : "",
  segment: proj.segment_id || "",
  product_category: proj.product_category || "",
  full_name: proj.client_full_name || "",
  designation: proj.client_designation || "",
  phone: proj.client_phone || "",
  email: proj.client_email || "",
  supervisor_name: proj.supervisor_name || "",
  supervisor_email: proj.supervisor_email || "",
  supervisor_phone: proj.supervisor_phone || "",
  applicator_name: proj.applicator_name || "",
  applicator_email: proj.applicator_email || "",
  applicator_phone: proj.applicator_phone || "",
t_id: proj.t_id || "",
  total_area_sqmtr: proj.total_area_sqmtr || "",
  created_by: localStorage.getItem("id"),
});
        }
      } catch (err) {
        console.error("[ProjectEdit] Error loading project for edit:", err);
      } finally {
        setLoading(false);
      }
    };
    loadEditData();
  }, [editId]);

  useEffect(() => {
    API.get("/admin/product-categories")
      .then((res) => { setProductCategories(res.data.data || []); })
      .catch((err) => console.error("Failed fetching product categories:", err));
  }, []);

  useEffect(() => {
    let rawRegion = "";
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      rawRegion = user?.region || localStorage.getItem("region") || "";
    } catch {
      rawRegion = localStorage.getItem("region") || "";
    }
    console.log("LOGIN USER REGION =", rawRegion);

    const list = rawRegion
      ? rawRegion.split(",").map((r) => r.trim()).filter(Boolean)
      : [];
    setUserRegions(list);

    if (!editId) {
      const defaultRegion = list.length > 0 ? list[0] : rawRegion;
      setFormData((prev) => ({
        ...prev,
        region: prev.region && list.includes(prev.region) ? prev.region : defaultRegion,
      }));
    }
  }, [editId]);

  const fetchAssignments = async () => {
    try {
      if (editId) return;
      setLoading(true);
      const token = localStorage.getItem("token");
      let endpoint = "";

if (localStorage.getItem("is_admin") === "1" || localStorage.getItem("is_admin") === "true") {
  endpoint = "/admin/project-assignments";
} else if (localStorage.getItem("dashboard_type") === "admin") {
        endpoint = `/admin/project-assignments-manager/${localStorage.getItem("id")}`;
      } else {
        endpoint = `/admin/project-assignments-engineer/${localStorage.getItem("id")}`;
      }
      const res = await API.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignments(res.data.data || []);
    } catch (err) {
      console.error("Failed downloading assignment configurations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssignments(); }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    API.get("/engineers", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        console.log("ENGINEERS API:", res.data);
        setEngineers(res.data.engineers || res.data.data || []);
      })
      .catch((err) => {
        console.error("ENGINEERS ERROR:", err);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    API.get("/admin/project-assignments/project-dropdown", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { setProjects(res.data.projects || []); })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    API.get("/admin/discussion-topics")
      .then((res) => { setSegments(res.data.data || []); })
      .catch((err) => console.error("Failed fetching segments:", err));
  }, []);

  // ── ALL HER HANDLERS — 100% UNCHANGED ────────────────────────────────────
const fetchNextProjectId = async (product, selectedRegion) => {
    if (!product || !selectedRegion || editId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await API.get(
        `/admin/next-project-id?product=${encodeURIComponent(product)}&region=${encodeURIComponent(selectedRegion)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.success && res.data?.projectId) {
        setFormData((prev) => ({
          ...prev,
          projectId: res.data.projectId,
        }));
      }
    } catch (err) {
      console.error("Project ID generation failed:", err);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Mobile Numbers
    if (name === "phone" || name === "supervisor_phone" || name === "applicator_phone") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }
    // TID — numbers only
    if (name === "t_id") {
      const hasInvalidChar = /\D/.test(value);
      setTidError(hasInvalidChar);
      newValue = value.replace(/\D/g, "");
      if (hasInvalidChar) {
        setTimeout(() => setTidError(false), 2000); // auto-hide after 2s
      }
    }

    // Total Area (Sqmtr) — numbers and one decimal point only
    if (name === "total_area_sqmtr") {
      newValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    }

    // Names
    if (
      name === "project" ||
      name === "full_name" ||
      name === "supervisor_name" ||
      name === "applicator_name"
    ) {
      newValue = value.replace(/[^A-Za-z0-9 ]/g, "");
    }

    // Remove multiple spaces
    newValue = newValue.replace(/\s{2,}/g, " ");

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (!editId) {
      if (name === "product_category") {
        if (newValue && formData.region) {
          fetchNextProjectId(newValue, formData.region);
        } else if (!newValue) {
          setFormData((prev) => ({ ...prev, projectId: "" }));
        }
      } else if (name === "region") {
        if (newValue && formData.product_category) {
          fetchNextProjectId(formData.product_category, newValue);
        }
      }
    }
  };

const handleSave = async () => {
    setSaveError("");
    const projectName = formData.project.trim();

    if (!projectName) {
      setSaveError("Project Name is required.");
      return;
    }
    if (!/^[A-Za-z0-9 ]+$/.test(projectName)) {
      setSaveError("Project Name can contain only letters, numbers and spaces.");
      return;
    }
    if (projectName.length < 3) {
      setSaveError("Project Name must be at least 3 characters.");
      return;
    }
    if (projectName.length > 100) {
      setSaveError("Project Name cannot exceed 100 characters.");
      return;
    }
    if (!formData.projectId) {
      setSaveError("Project ID is required.");
      return;
    }
    if (!formData.t_id || !String(formData.t_id).trim()) {
  setSaveError("T ID is required.");
  return;
}
    if (!formData.total_area_sqmtr || String(formData.total_area_sqmtr).trim() === "") {
      setSaveError("Total Area (Sqmtr) is required.");
      return;
    }
    if (isNaN(Number(formData.total_area_sqmtr)) || Number(formData.total_area_sqmtr) <= 0) {
      setSaveError("Total Area (Sqmtr) must be a positive number.");
      return;
    }
    if (!formData.segment) {
      setSaveError("Please select Segment.");
      return;
    }
    if (!formData.product_category) {
      setSaveError("Please select Product Category.");
      return;
    }
    if (!formData.engineer) {
      setSaveError("Please select Engineer.");
      return;
    }
    if (!formData.startDate) {
      setSaveError("Please select Start Date.");
      return;
    }
    if (!formData.endDate) {
      setSaveError("Please select End Date.");
      return;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setSaveError("End Date cannot be earlier than Start Date.");
      return;
    }
    if (!formData.supervisor_name || !formData.supervisor_name.trim()) {
  setSaveError("Supervisor Name is required.");
  return;
}
if (!formData.supervisor_phone || formData.supervisor_phone.trim().length !== 10) {
  setSaveError("Supervisor Phone Number must be a valid 10-digit number.");
  return;
}
if (!formData.supervisor_email || !formData.supervisor_email.trim()) {
  setSaveError("Supervisor Email is required.");
  return;
}
if (!formData.full_name || !formData.full_name.trim()) {
  setSaveError("Stakeholder Full Name is required.");
  return;
}
if (!formData.designation || !formData.designation.trim()) {
  setSaveError("Stakeholder Designation is required.");
  return;
}
if (!formData.phone || formData.phone.trim().length !== 10) {
  setSaveError("Stakeholder Phone Number must be a valid 10-digit number.");
  return;
}
if (!formData.email || !formData.email.trim()) {
  setSaveError("Stakeholder Email is required.");
  return;
}
    if (!formData.project || !formData.projectId || !formData.engineer) {
      setSaveError("Please fill all required fields");
      return;
    }
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (formData.startDate && (!dateRegex.test(formData.startDate) || parseInt(formData.startDate.split('-')[0], 10) < 1000 || parseInt(formData.startDate.split('-')[0], 10) > 9999)) {
      setSaveError("Please select a valid Start Date with a 4-digit year between 1000 and 9999.");
      return;
    }
    if (formData.endDate && (!dateRegex.test(formData.endDate) || parseInt(formData.endDate.split('-')[0], 10) < 1000 || parseInt(formData.endDate.split('-')[0], 10) > 9999)) {
      setSaveError("Please select a valid End Date with a 4-digit year between 1000 and 9999.");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editId) {
        const res = await API.put(`/admin/project-assignments/${editAssignmentId}`, formData, config);
        if (res.data.success) {
          setSaveSuccess("Project details updated successfully!");
          setTimeout(() => window.history.back(), 1200);
        }
      } else {
        const res = await API.post("/admin/project-assignments", formData, config);
        if (res.data.success) {
          setSaveSuccess("Project assigned successfully!");
          setFormData({
            project: "",
            projectId: "",
            t_id: "",
            total_area_sqmtr: "",
            region: formData.region || "",
            manager: localStorage.getItem("manager_id") || localStorage.getItem("id"),
            engineer: localStorage.getItem("id") || "",
            startDate: "",
            endDate: "",
            segment: "",
            product_category: "",
            full_name: "",
            designation: "",
            phone: "",
            email: "",
            supervisor_name: "", supervisor_email: "", supervisor_phone: "",
            applicator_name: "", applicator_email: "", applicator_phone: "",
            created_by: localStorage.getItem("id"),
          });
          fetchAssignments();
        }
      }
    } catch (err) {
      setSaveError(err.response?.data?.message || "Failed storing allocation entry parameters.");
    } finally {
      setSaving(false);
    }
  };

  // ── HER Layout switch — 100% UNCHANGED ───────────────────────────────────
  const Layout = layout === "engineer" ? EngineerLayout : AdminLayout;

  // ── KPI counts from her assignments state ─────────────────────────────────
  const stats = {
    total: assignments.length,
    active: assignments.filter(a => a.status === "Active").length,
    inProgress: assignments.filter(a => a.status === "In Progress").length,
    completed: assignments.filter(a => a.status === "Completed").length,
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — restyled with your design, her data bindings untouched
  // ═══════════════════════════════════════════════════════════════════════════
  const regionOptions = Array.from(new Set([
    ...userRegions,
    ...(formData.region ? [formData.region] : [])
  ])).filter(Boolean);

  return (
    <Layout>
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
        .pa-root * { font-family: 'Montserrat', sans-serif !important; }
        .pa-uid    { font-family: 'Montserrat', monospace !important; }
        .pa-row:hover td { background: ${T.violetFaint} !important; }
        .pa-submit:hover { opacity:0.9; transform:translateY(-1px); box-shadow:0 8px 24px rgba(92,45,145,0.35)!important; }
        .pa-submit { transition: all 0.18s ease !important; }
        @media(max-width:900px){
          .pa-kpi-grid { grid-template-columns:1fr 1fr !important; }
          .pa-form-2   { grid-template-columns:1fr !important; }
          .pa-form-3   { grid-template-columns:1fr !important; }
        }
        @media(max-width:560px){
          .pa-root-pad { padding:14px 12px !important; }
          .pa-kpi-grid { grid-template-columns:1fr 1fr !important; }
          .pa-hide-sm  { display:none !important; }
        }
          @media(max-width:560px){
          .pa-root-pad { padding:14px 12px !important; }
          .pa-kpi-grid { grid-template-columns:1fr 1fr !important; }
          .pa-hide-sm  { display:none !important; }
        }
@keyframes pa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pa-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }
      
      `}</style>

{/* ── SUCCESS TOAST (top-right, auto-dismisses) — same design as UserMaster ── */}
      {saveSuccess && (
<div style={{
          position: "fixed", top: 24, right: 24, zIndex: 99999,
          background: T.greenDark, color: "#fff",
          padding: "14px 18px", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(26,122,66,0.35)",
          display: "flex", alignItems: "center", gap: 12,
          minWidth: 280, maxWidth: 380,
          animation: "pa-toast-in 0.22s ease",
          fontFamily: "'Montserrat',sans-serif",
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}></span>
        <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{saveSuccess}</div>
            <div style={{ fontSize: 12, opacity: 0.82, marginTop: 2 }}>
              {editId ? "Redirecting to project list…" : "You can add another project below."}
            </div>
          </div>
          <button onClick={() => setSaveSuccess("")} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
            fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>✕</button>
        </div>
      )}

      <div className="pa-root pa-root-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ══ PAGE HEADER ═════════════════════════════════════════════════ */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Admin · Project Management
            </span>
          </div>
<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
{editId && (
    <button
      onClick={() => window.history.back()}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 8, padding: "0 16px", height: 36, borderRadius: 10,
        background: T.surface, border: `1.5px solid ${T.border}`,
        cursor: "pointer", color: T.dark, flexShrink: 0,
        fontSize: 13, fontWeight: 600, fontFamily: "'Montserrat',sans-serif",
      }}
    >
      <ArrowLeft size={16} />
      Back to Project List
    </button>
  )}
  <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
    {editId ? "Edit Project" : "New Project"}
  </h1>
  <span style={{
    background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
    color: "#fff", fontSize: 9, fontWeight: 700,
    padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
    textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
  }}>ADMIN</span>
</div>
          <p style={{ color: T.muted, fontSize: 13, margin: 0 }}>
            {editId ? "Update project details and team assignments" : "Assign engineers to a new project and configure client detail"}
          </p>
        </div>

{/* ══ KPI STRIP ═══════════════════════════════════════════════════ */}
{!editId && (
  <div className="pa-kpi-grid" style={{
    display: "grid", gridTemplateColumns: "repeat(3,1fr)",
    gap: 16, marginBottom: 24,
  }}>
    {[
      { label: "Total Projects", value: stats.total, icon: <FolderKanban size={22} />, color: T.violet, bg: T.violetLight, border: "#D8C8F5" },
      { label: "Active", value: stats.active, icon: <RefreshCw size={22} />, color: T.greenDark, bg: T.greenLight, border: "#B0E8C8" },
      { label: "In Progress", value: stats.inProgress, icon: <Settings size={22} />, color: T.orange, bg: T.orangeLight, border: "#F5C898" },
      
    ].map(s => (
      <div key={s.label} style={{
        background: T.surface, borderRadius: 16,
        border: `1px solid ${T.borderSolid}`,
        padding: "20px 22px",
        display: "flex", alignItems: "center", gap: 14,
        boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", right: -10, top: -10, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
        <div style={{
          width: 46, height: 46, borderRadius: 13,
          background: s.bg, border: `1.5px solid ${s.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0, position: "relative",
        }}>{s.icon}</div>
        <div style={{ position: "relative" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
          <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>
            {loading ? "…" : s.value}
          </div>
        </div>
      </div>
    ))}
  </div>
)}

        {/* ══ SECTION A — Project Details ═════════════════════════════════ */}
        <FormCard accentLeft={T.violet} accentRight={T.violetMid}>
<SectionHeader icon={<ClipboardList size={16} />} title="Project Details" subtitle="Basic project identity and categorisation" />
          <div className="pa-form-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18 }}>

            {/* Segment — her binding */}
            <Select label="Segment" name="segment" value={formData.segment} onChange={handleChange}>
              <option value="">Select Segment</option>
             {[...segments].sort((a, b) => (a.title || "").localeCompare(b.title || "")).map(seg => <option key={seg.id} value={seg.id}>{seg.title}</option>)}
            </Select>

            {/* Project Name — her binding */}
            <Input label="Project Name" type="text" name="project" value={formData.project} onChange={handleChange} placeholder="e.g. Bridge Construction Phase 1" />

            {/* Product Category — her binding + her auto-ID logic via handleChange */}
            <Select label="Product Category" name="product_category" value={formData.product_category} onChange={handleChange}>
              <option value="">Select Product Category</option>
              {productCategories.map((item, idx) => <option key={idx} value={item.product_category}>{item.product_category}</option>)}
            </Select>

          {/* Project ID — readOnly auto-generated */}
<Input label="Project ID" type="text" name="projectId" value={formData.projectId} readOnly placeholder="Auto Generated" />

{/* Region — dropdown supporting single or multiple regions */}
            <Select label="Region" name="region" value={formData.region} onChange={handleChange} disabled={Boolean(editId)}>
              {regionOptions.length === 0 && <option value="">No Region Available</option>}
              {regionOptions.map((reg, idx) => (
                <option key={idx} value={reg}>{reg}</option>
              ))}
            </Select>
{/* T ID — mandatory manual input, numbers only */}
<div>
  <Input label="TID " type="text" name="t_id" value={formData.t_id || ""} onChange={handleChange} placeholder="e.g. 12345" maxLength={100} />
  <div style={{ fontSize: 11, marginTop: 5, color: tidError ? T.red : T.faint, fontFamily: "'Montserrat',sans-serif", fontWeight: tidError ? 700 : 400 }}>
    {tidError ? " Only numbers are allowed in TID." : ""}
  </div>
</div>

{/* Total Area (Sqmtr) — mandatory manual input, numbers/decimal only */}
<Input label="Total Area (Sqmtr) " type="text" name="total_area_sqmtr" value={formData.total_area_sqmtr || ""} onChange={handleChange} placeholder="e.g. 1500.50" maxLength={15} />

{/* Engineer — hidden (display:none) for engineer view only; admin/manager still see it */}
<div style={{ display: layout === "engineer" ? "none" : "block" }}>
  <Select label="Engineer" name="engineer" value={formData.engineer} onChange={handleChange}>
    <option value="">Select Engineer</option>
    {engineers.map(eng => <option key={eng.id} value={eng.id}>{eng.name}</option>)}
  </Select>
</div>

          </div>
        </FormCard>


        {/* ══ SECTION C — Timeline ════════════════════════════════════════ */}
        <FormCard accentLeft={T.blue} accentRight="#60A5FA">
          <SectionHeader icon={<CalendarDays size={16} />} title="Timeline" subtitle="Set project start and end dates" />
          <div className="pa-form-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <Input label="Start Date" type="date" name="startDate" value={formData.startDate} onChange={handleChange} min="1000-01-01" max="9999-12-31" />
            <Input label="End Date" type="date" name="endDate" value={formData.endDate} onChange={handleChange} min="1000-01-01" max="9999-12-31" />
          </div>
        </FormCard>

        {/* ══ SECTION — Site Supervisor ══════════════════════════════════ */}
        <FormCard accentLeft={T.violet} accentRight={T.violetMid}>
<SectionHeader icon={<HardHat size={16} />} title="Site Supervisor" subtitle="Supervisor contact details for this project" />
          <div className="pa-form-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18 }}>
            <Input label="Name" type="text" name="supervisor_name" maxLength={50} value={formData.supervisor_name || ""} onChange={handleChange} placeholder="e.g. Rajesh Kumar" />
            <Input label="Phone Number" type="text" name="supervisor_phone" maxLength={10} value={formData.supervisor_phone || ""} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
            <Input label="Email Address" type="email" name="supervisor_email" maxLength={100} value={formData.supervisor_email || ""} onChange={handleChange} placeholder="e.g. rajesh@company.com" />
          </div>
        </FormCard>

        {/* ══ SECTION — Applicator ═══════════════════════════════════════ */}
        <FormCard accentLeft={T.orange} accentRight={T.gold}>
          <SectionHeader icon={<HardHat size={16} />} title="Applicator" subtitle="Applicator contact details for this project" />
          <div className="pa-form-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18 }}>
            <Input label="Name" type="text" name="applicator_name" maxLength={50} value={formData.applicator_name || ""} onChange={handleChange} placeholder="e.g. Suresh Patil" />
            <Input label="Phone Number" type="text" name="applicator_phone" maxLength={10} value={formData.applicator_phone || ""} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
            <Input label="Email Address" type="email" name="applicator_email" maxLength={100} value={formData.applicator_email || ""} onChange={handleChange} placeholder="e.g. suresh@company.com" />
          </div>
        </FormCard>

        {/* ══ SECTION D — Stakeholder Information ════════════════════════ */}
        <FormCard accentLeft={T.green} accentRight="#4ADE80">
          <SectionHeader icon={<Users size={16} />} title="Stakeholder Information" subtitle="Client contact details — submitted with the project assignment" />
          <div style={{ background: T.violetFaint, border: `1.5px solid ${T.violetLight}`, borderRadius: 14, padding: "20px 20px" }}>
            <div className="pa-form-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              {/* Her field names: full_name, designation, phone, email */}
              <Input label="Full Name" type="text" name="full_name" maxLength={100} value={formData.full_name} onChange={handleChange} placeholder="e.g. John Smith" />
              <Input label="Designation" type="text" name="designation"maxLength={100} value={formData.designation} onChange={handleChange} placeholder="e.g. CEO" />
              <Input label="Phone Number" type="text" name="phone" maxLength={10} value={formData.phone} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
              <Input label="Email Address" type="email" name="email" maxLength={50} value={formData.email} onChange={handleChange} placeholder="e.g. john@company.com" />
            </div>
          </div>
        </FormCard>

{/* ── ERROR BANNER — same design as UserMaster ── */}
        {saveError && (
          <div style={{
            marginBottom: 16, padding: "12px 16px", borderRadius: 10,
            background: "#FEF2F2", border: `1.5px solid #FECACA`,
            display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <span style={{ flexShrink: 0, display: "inline-flex" }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 2, fontFamily: "'Montserrat',sans-serif" }}>Could not save project</div>
              <div style={{ fontSize: 12, color: "#B91C1C", lineHeight: 1.5, fontFamily: "'Montserrat',sans-serif" }}>{saveError}</div>
            </div>
            <button onClick={() => setSaveError("")} style={{
              marginLeft: "auto", background: "none", border: "none",
              color: "#DC2626", cursor: "pointer", padding: 0, flexShrink: 0, display: "flex",
            }}>✕</button>
          </div>
        )}

        {/* ══ SUBMIT ══════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <button
            className="pa-submit"
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saving
                ? `linear-gradient(135deg,${T.muted},#9E8BB5)`
                : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
              color: "#fff", border: "none",
              padding: "13px 32px", borderRadius: 12,
              fontSize: 14, fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              boxShadow: saving ? "none" : `0 4px 16px ${T.violet}40`,
              fontFamily: "'Montserrat',sans-serif",
              display: "flex", alignItems: "center", gap: 8,
              opacity: saving ? 0.85 : 1,
              transition: "all 0.18s",
            }}
          >
            {saving ? (
  <>
    <span style={{
      display: "inline-block", width: 14, height: 14,
      border: "2px solid rgba(255,255,255,0.35)",
      borderTopColor: "#fff", borderRadius: "50%",
      animation: "pa-spin 0.7s linear infinite",
    }} />
    Please wait…
  </>
) : (
   editId ? "Update Project" : "Save Project"
)}
          </button>
          {editId && (
            <button
              onClick={() => window.history.back()}
              style={{
                background: T.surface, color: T.muted,
                border: `1.5px solid ${T.border}`,
                padding: "12px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: "'Montserrat',sans-serif",
              }}
            >Cancel</button>
          )}
        </div>



      </div>
    </Layout>
  );
}

export default ProjectAssignment;